import { Component, OnDestroy, inject } from '@angular/core';
import {VwuiModalConfig} from '@bvgo-ict/vwui-angular';
import {ProjectReferenceDetailResponse} from '../../models/project-reference-detail.response';
import {PrintDataService} from '../../services/print-data.service';
import {BehaviorSubject, combineLatest, Observable, of, Subscription} from 'rxjs';
import {finalize, pairwise, switchMap} from 'rxjs/operators';
import {FormControl, FormGroup, FormRecord, Validators} from '@angular/forms';
import {PrintData} from '../../models/print-data';
import {ProjectAttribute} from '../../models/project-attribute';
import {controlValueChanges} from '../../utils/form-control-value';
import {ProjectReferenceAttributeResponseFlattened} from '../../models/project-reference-attribute-response';
import {ProjectReferenceContractAttributesResponse} from '../../models/project-reference-contract-attributes-response';
import {PhotoLayouts} from './select-photo-layout/select-photo-layout.component';

@Component({
    selector: 'app-project-reference-download-select-info-modal',
    templateUrl: './project-reference-download-select-info-modal.component.html',
    standalone: false
})
export class ProjectReferenceDownloadSelectInfoModalComponent implements OnDestroy {
    private modalConfig = inject<VwuiModalConfig<{
    projectReference: ProjectReferenceDetailResponse;
    title: string;
}>>(VwuiModalConfig);
    private printDataLoadingService = inject(PrintDataService);

    private subscriptions: Subscription[] = [];

    projectReference$ = new BehaviorSubject<ProjectReferenceDetailResponse | null>(null);

    dataLoading$ = new BehaviorSubject(true);
    unfilteredData$: Observable<PrintData | null> = this.projectReference$.pipe(
        switchMap((projectReference) => {
            this.dataLoading$.next(true);
            if (!projectReference) {
                return of(null);
            }

            return this.printDataLoadingService.loadUnfilteredPrintData(projectReference.id).pipe(
                finalize(() => this.dataLoading$.next(false)),
            );
        })
    );

    form = new FormGroup({
        contractAttributes: new FormGroup({
            selected: new FormControl(true, {nonNullable: true}),
            fields: new FormRecord({})
        }),
        attributes: new FormGroup({
            selected: new FormControl(true, {nonNullable: true}),
            fields: new FormRecord({})
        }),
        relations: new FormControl(true, {nonNullable: true}),
        teamMembers: new FormControl(true, {nonNullable: true}),
        selectedPhotos: new FormControl<number[]>([], {
            validators: Validators.maxLength(5),
            nonNullable: true
        }),
        photoLayout: new FormControl(null, { validators: [Validators.required]})
    })

    constructor() {
        const modalConfig = this.modalConfig;

        if (modalConfig.data?.projectReference) {
            this.projectReference$.next(modalConfig.data.projectReference);
        } else {
            throw Error('Property projectReference is missing');
        }

        this.subscriptions.push(controlValueChanges(this.form).pipe(
            pairwise()
        ).subscribe(([previousValue, currentValue]) => {
            // This code handles selecting (and de-selecting) all fields in the contractAttributes and attributes groups based on the groups top-level selected field
            const patchValue: {
                contractAttributes?: { fields: { [key: string]: boolean } }
                attributes?: { fields: { [key: string]: boolean } }
            } = {};

            const contractSelected = currentValue.contractAttributes?.selected || false;
            if (previousValue.contractAttributes!!.selected !== contractSelected) {
                // Add all fields from contractAttributes to patchValue
                patchValue['contractAttributes'] = {
                    fields: Object.keys(this.form.controls.contractAttributes.controls.fields.controls).reduce((result, key) => {
                        const fieldDisabled = this.form.controls.contractAttributes.controls.fields.controls[key].disabled;
                        // Keep the field on false if it is disabled
                        result[key] = fieldDisabled ? false : contractSelected;
                        return result;
                    }, {} as { [key: string]: boolean })
                }
            }
            const attributesSelected = currentValue.attributes?.selected || false;
            if (previousValue.attributes!!.selected !== attributesSelected) {
                // Add all fields from attributes to patchValue
                patchValue['attributes'] = {
                    fields: Object.keys(this.form.controls.attributes.controls.fields.controls).reduce((result, key) => {
                        const fieldDisabled = this.form.controls.attributes.controls.fields.controls[key].disabled;
                        // Keep the field on false if it is disabled
                        result[key] = fieldDisabled ? false : attributesSelected;
                        return result;
                    }, {} as { [key: string]: boolean })
                }
            }

            this.form.patchValue(patchValue, {emitEvent: false});
        }));

        this.subscriptions.push(combineLatest([
            this.unfilteredData$,
            controlValueChanges(this.form)
        ]).subscribe(([printData]) => {
            if (printData) {
                this.emitUpdatedPrintConfig(printData);
            }
        }));
    }

    ngOnDestroy() {
        this.subscriptions.forEach(subscription => subscription.unsubscribe());
        this.subscriptions = [];
    }

    getFieldControl(tab: 'contractAttributes'|'attributes', field: string, fieldValue: any): FormControl {
        const formRecord = this.form.controls[tab].controls.fields as FormRecord<FormControl<boolean>>;
        let control: FormControl<boolean> | null = formRecord.get(field) as FormControl<boolean> | null
        if (control === null) {
            const fieldHasValue = fieldValue !== null && (
                typeof fieldValue === 'number' || typeof fieldValue === 'string' || (
                    typeof fieldValue === 'object' && 'selectedValueId' in fieldValue && fieldValue.selectedValueId !== null
                )
            );

            // If attribute field does not have value add it as a disabled control
            control = new FormControl(fieldHasValue ? true: {value: false, disabled: true}, {nonNullable: true});
            formRecord.addControl(field, control);
            return control;
        } else {
            return control;
        }
    }

    getFieldLabel(field: string, fieldValue: any) {
        if (fieldValue !== null && typeof fieldValue === 'object' && 'title' in fieldValue) {
            return fieldValue.title;
        } else if (field === 'contractPrice') {
            return 'Aanneemsom';
        } else if (field === 'additionalWork') {
            return 'Meer- en minderwerk';
        } else {
            // Let the attributeKeyTranslate pipe handle the translation
            return null;
        }
    }

    async print() {
        if (this.dataLoading$.value || !this.form.valid) {
            return;
        }

        const originalDocumentTitle = window.document.title;

        if (this.modalConfig.data) {
            window.document.title = `PRS_${this.modalConfig.data.title}`;
        }

        setTimeout(() => {
            // Trigger print after Angular has had a chance to re-render the printData
            window.print();
            window.document.title = originalDocumentTitle;
        }, 0);

    }

    private emitUpdatedPrintConfig(printData: PrintData) {
        const formVal = this.form.getRawValue()
        const printDataToUpdate = {...printData};


        if (!formVal.relations) {
            printDataToUpdate.relations = null;
        }
        if (!formVal.teamMembers) {
            printDataToUpdate.teamMembers = null;
        }
        if (!formVal.attributes.selected) {
            printDataToUpdate.attributes = null;
        } else if (printDataToUpdate.attributes !== null) {
            for (let key in (printDataToUpdate.attributes || {})) {
                if (formVal.attributes.fields[key] === false) {
                    printDataToUpdate.attributes[key as keyof ProjectReferenceAttributeResponseFlattened] = null;
                }
            }
        }

        if (!formVal.contractAttributes.selected) {
            printDataToUpdate.contractAttributes = null;
        } else if (printDataToUpdate.contractAttributes !== null) {
            for (let key in (printDataToUpdate.contractAttributes || {})) {
                if (formVal.contractAttributes.fields[key] === false) {
                    printDataToUpdate.contractAttributes[key as keyof ProjectReferenceContractAttributesResponse] = null;
                }
            }
        }

        if (formVal.photoLayout !== null) {
            this.printDataLoadingService.printConfig$.next({
                ...printDataToUpdate,
                photos: printDataToUpdate.photos.filter((photo) => formVal.selectedPhotos.includes(photo.id)),
                photoLayout: formVal.photoLayout || '',
            });
        }
    }
}
